// client/src/pages/admin/AdminWholesalers.js
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Building, Search, Filter, Download, Eye, Ban, CheckCircle,
  TrendingUp, UserX, UserPlus, ChevronDown, ChevronUp, Award
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import ConfirmModal from '../../components/common/ConfirmModal';
import { adminAPI } from '../../services/api';

const AdminWholesalers = () => {
  const navigate = useNavigate();
  const [wholesalers, setWholesalers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [serviceAreaFilter, setServiceAreaFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    fetchData();
  }, [searchTerm, statusFilter, tierFilter, serviceAreaFilter, sortBy, sortOrder]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const params = {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        tier: tierFilter !== 'all' ? tierFilter : undefined,
        serviceArea: serviceAreaFilter !== 'all' ? serviceAreaFilter : undefined,
        sortBy,
        sortOrder
      };

      const [wholesalersRes, statsRes] = await Promise.all([
        adminAPI.getWholesalerUsers(params),
        adminAPI.getWholesalerStats()
      ]);

      setWholesalers(wholesalersRes.data.wholesalers || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUnblock = async (wholesalerId, currentStatus) => {
    const action = currentStatus ? 'block' : 'unblock';
    setConfirmModal({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Wholesaler`,
      message: `Are you sure you want to ${action} this wholesaler?`,
      icon: 'warning',
      confirmLabel: action.charAt(0).toUpperCase() + action.slice(1),
      confirmColor: 'yellow',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await adminAPI.blockUnblockWholesaler(wholesalerId, { isActive: !currentStatus });
          fetchData();
        } catch (err) {
          console.error('Error updating wholesaler status:', err);
          toast.error('Failed to update wholesaler status');
        }
      }
    });
  };

  const handleExportCSV = () => {
    const headers = [
      'Company Name',
      'Contact Person',
      'Email',
      'Phone',
      'Service Areas',
      'Subscription Tier',
      'Products',
      'Total Orders',
      'Total Revenue',
      'Status',
      'Registered Date'
    ];

    const rows = wholesalers.map(w => [
      w.companyName,
      w.contactPerson,
      w.email,
      w.phone,
      w.serviceAreas.join('; '),
      w.subscriptionTier,
      w.productCount,
      w.totalOrders,
      `€${w.totalRevenue.toFixed(2)}`,
      w.isActive ? 'Active' : 'Blocked',
      format(new Date(w.createdAt), 'yyyy-MM-dd')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wholesalers-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'starter':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'professional':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'enterprise':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (isActive) => {
    return isActive
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">Loading wholesalers...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Wholesaler Users</h1>
          <p className="text-gray-600">Manage and monitor all wholesaler accounts</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Wholesalers</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalWholesalers}</p>
                </div>
                <Building className="w-12 h-12 text-purple-500" />
              </div>
            </div>

            <div className="bg-green-50 rounded-lg shadow-md p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-800 mb-1">Active</p>
                  <p className="text-3xl font-bold text-green-900">{stats.activeWholesalers}</p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg shadow-md p-6 border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-800 mb-1">New This Month</p>
                  <p className="text-3xl font-bold text-yellow-900">{stats.newThisMonth}</p>
                </div>
                <UserPlus className="w-12 h-12 text-yellow-600" />
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg shadow-md p-6 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-800 mb-1">Total Products</p>
                  <p className="text-3xl font-bold text-blue-900">{stats.totalProducts}</p>
                </div>
                <Award className="w-12 h-12 text-blue-600" />
              </div>
            </div>

            <div className="bg-red-50 rounded-lg shadow-md p-6 border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-800 mb-1">Blocked</p>
                  <p className="text-3xl font-bold text-red-900">{stats.blockedWholesalers}</p>
                </div>
                <UserX className="w-12 h-12 text-red-600" />
              </div>
            </div>
          </div>
        )}

        {/* Tier Distribution */}
        {stats && stats.tierDistribution && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Subscription Tier Distribution</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-800">{stats.tierDistribution.starter}</p>
                <p className="text-sm text-gray-600">Starter</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-800">{stats.tierDistribution.professional}</p>
                <p className="text-sm text-blue-600">Professional</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-800">{stats.tierDistribution.enterprise}</p>
                <p className="text-sm text-purple-600">Enterprise</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by company, email, or contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>

            {/* Tier Filter */}
            <div className="w-full lg:w-48">
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Tiers</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            {/* Sort */}
            <div className="w-full lg:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="createdAt">Registration Date</option>
                <option value="companyName">Company Name</option>
                <option value="totalOrders">Total Orders</option>
                <option value="totalRevenue">Total Revenue</option>
                <option value="productCount">Product Count</option>
              </select>
            </div>

            {/* Sort Order */}
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
            >
              {sortOrder === 'asc' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            </button>

            {/* Export */}
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {wholesalers.length} {wholesalers.length === 1 ? 'wholesaler' : 'wholesalers'}
        </div>

        {/* Wholesalers Table */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {wholesalers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No wholesalers found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Products
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Orders / Revenue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registered
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {wholesalers.map((wholesaler) => (
                    <tr key={wholesaler.userId} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{wholesaler.companyName}</div>
                          <div className="text-sm text-gray-500">{wholesaler.serviceAreas.slice(0, 2).join(', ')}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm text-gray-900">{wholesaler.contactPerson}</div>
                          <div className="text-sm text-gray-500">{wholesaler.email}</div>
                          <div className="text-sm text-gray-500">{wholesaler.phone}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border capitalize ${getTierColor(wholesaler.subscriptionTier)}`}>
                          {wholesaler.subscriptionTier}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {wholesaler.productCount} products
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {wholesaler.totalOrders} orders
                          </div>
                          <div className="text-sm text-gray-500">
                            €{wholesaler.totalRevenue.toFixed(2)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(wholesaler.isActive)}`}>
                          {wholesaler.isActive ? 'Active' : 'Blocked'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {format(new Date(wholesaler.createdAt), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/wholesaler/${wholesaler.wholesalerId}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleBlockUnblock(wholesaler.wholesalerId, wholesaler.isActive)}
                            className={`p-2 rounded-lg transition-colors ${
                              wholesaler.isActive
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={wholesaler.isActive ? 'Block Wholesaler' : 'Unblock Wholesaler'}
                          >
                            {wholesaler.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {wholesalers.map((wholesaler) => (
                <div key={wholesaler.userId} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{wholesaler.companyName}</h3>
                      <p className="text-sm text-gray-500">{wholesaler.serviceAreas.join(', ')}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(wholesaler.isActive)}`}>
                      {wholesaler.isActive ? 'Active' : 'Blocked'}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-3">
                    <div>
                      <span className="text-gray-500">Contact:</span>{' '}
                      <span className="text-gray-900">{wholesaler.contactPerson}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>{' '}
                      <span className="text-gray-900">{wholesaler.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Tier:</span>{' '}
                      <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getTierColor(wholesaler.subscriptionTier)}`}>
                        {wholesaler.subscriptionTier}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Products:</span>{' '}
                      <span className="text-gray-900">{wholesaler.productCount}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Orders:</span>{' '}
                      <span className="text-gray-900">{wholesaler.totalOrders} (€{wholesaler.totalRevenue.toFixed(2)})</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Registered:</span>{' '}
                      <span className="text-gray-900">{format(new Date(wholesaler.createdAt), 'MMM dd, yyyy')}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/wholesaler/${wholesaler.wholesalerId}`)}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </button>
                    <button
                      onClick={() => handleBlockUnblock(wholesaler.wholesalerId, wholesaler.isActive)}
                      className={`px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        wholesaler.isActive
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {wholesaler.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      {wholesaler.isActive ? 'Block' : 'Unblock'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminWholesalers;